import React from 'react';
import {
    Ad,
    AppContent,
    baseUnit,
    DisableMainCategory,
    ErrorBoundary,
    FourHC,
    PaginationButtonGroup,
    Section,
    SectionOnTopTitle,
    ThreeVC,
    TrianglesIconForDivider,
    getArticlesInChunks,
} from 'mm-ui-components';
import { Store } from 'redux';
import { useSelector } from 'react-redux';
import { css, StyleSheet } from 'aphrodite/no-important';

import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { Page } from '../Page';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import {
    getFeedSectionArticles,
    getMetadataDescription,
    getMetadataImage,
    getMetadataTitle,
    getPagination,
    getTopSectionImage,
    getTopSectionSubtitle,
    getTopSectionTitle,
    getHreflangData,
} from '../../store/template/feedPage/feedPageNairobi/feedPageNairobi.selectors';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import { getAdSlotId, getAdSlotOnSuccess } from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';
import { getPageMetaText } from '../templates.utils';

interface FeedPageProps {
    store: Store<AppState, AppActions>;
    errorProvider: ErrorReporter;
}

const createStyle = () => StyleSheet.create({
    sectionsWrapper: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridRowGap: `${baseUnit}px`,
        justifyItems: 'center',
        marginTop: `${baseUnit * 0.25}px`,
    },
});

const generateThreeVCAndFourHC = (articles: Array<any>) => {
    return getArticlesInChunks(articles, 7).map((item: Array<any>) => {
     return (
       <>
         <Section>
           <ThreeVC cards={item.slice(0, 3)} paddingFactors={{ large: 1, medium: 1, small: 0.5 }} />
         </Section>

         <Section>
           <FourHC cards={item.slice(3)} showDivider dividerIcon={TrianglesIconForDivider} paddingFactors={{ large: 1, medium: 1, small: 0.5 }} />
         </Section>
       </>
    );
    });
};

export const FeedPageNairobiComponent: React.FunctionComponent<FeedPageProps> = ({ store, errorProvider }) => {
    useMeasureHydrateTime();
    return (
      <Page store={store} errorProvider={errorProvider}>
        <ErrorBoundary>
          <FeedPageNairobiContent />
        </ErrorBoundary>
      </Page>
    );
};

export const FeedPageNairobiContent: React.FunctionComponent = () => {
    const { prevButtonText, prevButtonURL, nextButtonText, nextButtonURL, currentPage } = useSelector(getPagination);
    const metaTitle = useSelector(getMetadataTitle);
    const metaDescription = useSelector(getMetadataDescription);
    const pageImage = useSelector(getMetadataImage);
    const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
    const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
    const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
    const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
    const topSectionTitle = useSelector(getTopSectionTitle);
    const topSectionSubtitle = useSelector(getTopSectionSubtitle);
    const topSectionImage = useSelector(getTopSectionImage);
    const articles = useSelector(getFeedSectionArticles) || [];
    const hreflangData = useSelector(getHreflangData) || [];
    const style = createStyle();
    return (
      <React.Fragment>
        <MetaTags {...getPageMetaText(metaTitle, metaDescription, currentPage)} articles={articles} pageImage={pageImage} alternateUrls={hreflangData} />
        <HeadScripts />
        <Theme>
          <DisableMainCategory>
            <AppContent>
              <NavigationLayout mainWrapperConfigThemeKey="mainContent">
                <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight />
                <div className={css(style.sectionsWrapper)}>

                  <Section themeConfigurationPropKey="firstSectionMaxWidth">
                    <SectionOnTopTitle title={topSectionTitle} subtitle={topSectionSubtitle} image={topSectionImage} />
                  </Section>
                  {generateThreeVCAndFourHC(articles)}
                </div>
                <PaginationButtonGroup prevText={prevButtonText} prevURL={prevButtonURL} nextText={nextButtonText} nextURL={nextButtonURL} />
                <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
              </NavigationLayout>
            </AppContent>
          </DisableMainCategory>
        </Theme>
      </React.Fragment>
    );
};
